/**
 * Platform Admin → Buntu Broadcasts
 * Compose + history for super-admin messages (parity with tenant Settings → Buntu → Messages).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare, RefreshCw, Send } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface TenantTarget {
  tenant_id: string;
  code: string;
  name: string;
  status: string;
  client_admin_count: number;
}

interface PlatformMessage {
  id: string;
  body: string;
  priority: string;
  delivery: string;
  sticky_minutes?: number;
  audience: string;
  status: string;
  sent_at?: string;
  created_at?: string;
  created_by?: string;
  delivered_count?: number;
  dismissed_count?: number;
  tenant_code?: string;
  tenant_name?: string;
}

export default function PlatformBuntuBroadcasts() {
  const toast = useToast();
  const [targets, setTargets] = useState<TenantTarget[]>([]);
  const [messages, setMessages] = useState<PlatformMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [allTenants, setAllTenants] = useState(true);
  const [compose, setCompose] = useState({
    body: '',
    priority: 'important',
    delivery: 'until_dismissed',
    sticky_minutes: 5,
    audience: 'client_admins',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [targetsRes, messagesRes] = await Promise.all([
        apiService.platformBuntuAdminTargets(),
        apiService.platformListBuntuMessages(),
      ]);
      const items = targetsRes?.data?.items || targetsRes?.items || [];
      setTargets(Array.isArray(items) ? items : []);
      const msgs = messagesRes?.data?.items || messagesRes?.items || [];
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load Buntu broadcasts');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const adminTotal = useMemo(
    () => targets.reduce((n, t) => n + (t.client_admin_count || 0), 0),
    [targets]
  );

  const toggleTenant = (id: string) => {
    setSelectedTenantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    const body = compose.body.trim();
    if (!body) {
      toast.error('Message body is required');
      return;
    }
    if (!allTenants && selectedTenantIds.length === 0) {
      toast.error('Select at least one tenant, or choose All tenants');
      return;
    }
    setSending(true);
    try {
      const res = await apiService.platformBuntuBroadcast({
        body,
        priority: compose.priority,
        delivery: compose.delivery,
        sticky_minutes: compose.sticky_minutes,
        audience: compose.audience,
        tenant_ids: allTenants ? undefined : selectedTenantIds,
      });
      const n = res?.data?.total_sent ?? res?.total_sent ?? 0;
      const skipped = res?.data?.skipped?.length ?? 0;
      toast.success(
        skipped ? `Sent to ${n} tenant(s) (${skipped} skipped)` : `Sent to ${n} tenant(s)`,
        'Buntu broadcast'
      );
      setCompose((c) => ({ ...c, body: '' }));
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await apiService.platformCancelBuntuMessage(id);
      toast.success('Message cancelled');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Cancel failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={22} className="text-indigo-600" />
            Buntu Broadcasts
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Send platform messages through Buntu to Client Admins or all Hub users across tenants —
            same controls as tenant Settings → Buntu → Messages.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-8">Loading…</p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-5">
          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Compose</h3>
            <p className="text-xs text-slate-500">
              {targets.length} tenant(s) · {adminTotal} Client Admin login(s) available for admin-only
              audience.
            </p>
            <textarea
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[120px] bg-white"
              placeholder="e.g. Platform maintenance tonight 22:00 UTC — warn your users."
              value={compose.body}
              maxLength={500}
              onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
            />
            <div className="text-right text-[11px] text-slate-400">{compose.body.length}/500</div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Priority</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white"
                  value={compose.priority}
                  onChange={(e) => setCompose((c) => ({ ...c, priority: e.target.value }))}
                >
                  <option value="info">Info</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Delivery</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white"
                  value={compose.delivery}
                  onChange={(e) => setCompose((c) => ({ ...c, delivery: e.target.value }))}
                >
                  <option value="once">Show once</option>
                  <option value="until_dismissed">Until dismissed</option>
                  <option value="sticky">Sticky</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-500">Audience</label>
              <select
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white"
                value={compose.audience}
                onChange={(e) => setCompose((c) => ({ ...c, audience: e.target.value }))}
              >
                <option value="client_admins">Client Admins only (per tenant)</option>
                <option value="all">All Hub users in selected tenants</option>
              </select>
            </div>

            {compose.delivery === 'sticky' && (
              <div>
                <label className="text-xs text-slate-500">Sticky minutes</label>
                <input
                  type="number"
                  min={1}
                  max={240}
                  className="mt-1 block w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white"
                  value={compose.sticky_minutes}
                  onChange={(e) =>
                    setCompose((c) => ({
                      ...c,
                      sticky_minutes: Math.max(1, Math.min(240, Number(e.target.value) || 5)),
                    }))
                  }
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs text-slate-500">Tenants</label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={allTenants}
                  onChange={(e) => {
                    setAllTenants(e.target.checked);
                    if (e.target.checked) setSelectedTenantIds([]);
                  }}
                />
                All active tenants
              </label>
              {!allTenants && (
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                  {targets.map((t) => (
                    <label
                      key={t.tenant_id}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTenantIds.includes(t.tenant_id)}
                        onChange={() => toggleTenant(t.tenant_id)}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-slate-800">{t.name}</span>
                        <span className="ml-2 font-mono text-[11px] text-slate-400">{t.code}</span>
                      </span>
                      <span className="text-[11px] text-slate-400 shrink-0">
                        {t.client_admin_count} admin(s)
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !compose.body.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              <Send size={16} />
              {sending ? 'Sending…' : 'Send broadcast'}
            </button>
          </div>

          <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Broadcast history</h3>
              <span className="text-xs text-slate-400">{messages.length} message(s)</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
              {messages.length === 0 && (
                <div className="p-6 text-sm text-slate-500">No platform broadcasts yet.</div>
              )}
              {messages.map((m) => (
                <div key={m.id} className="p-4 flex gap-3 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`text-[11px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          m.priority === 'urgent'
                            ? 'bg-red-100 text-red-700'
                            : m.priority === 'important'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {m.priority}
                      </span>
                      <span className="text-[11px] text-slate-500">{m.status}</span>
                      <span className="text-[11px] text-slate-500">{m.audience}</span>
                      <span className="text-[11px] font-mono text-indigo-600">
                        {m.tenant_code || '—'}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        seen {m.delivered_count ?? 0} · dismissed {m.dismissed_count ?? 0}
                      </span>
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.body}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {m.tenant_name || ''}
                      {m.created_by ? ` · ${m.created_by}` : ''}
                      {m.sent_at ? ` · ${new Date(m.sent_at).toLocaleString()}` : ''}
                    </p>
                  </div>
                  {(m.status === 'sent' || m.status === 'scheduled' || m.status === 'draft') && (
                    <button
                      type="button"
                      onClick={() => handleCancel(m.id)}
                      className="text-xs text-slate-500 hover:text-red-600 shrink-0"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
